import { useRef, useState } from 'react'
import { Check, ExternalLink, MessageSquare, RotateCcw } from 'lucide-react'
import { InfoCard } from '@/components/ui/InfoCard'
import { TextBackLink } from '@/components/ui/BackButton'
import {
  useAssignIssue,
  useReopenIssue,
  useUpdateIssuePriority,
  useUpdateIssueStatus,
} from '../../hooks/useSystemIssues'
import { Avatar } from '../../../occurrences/components/common/Avatar'
import { errorToast } from '../../../occurrences/utils/error-toast'
import { AssignDeveloperPicker } from '../shared/AssignDeveloperPicker'
import { DotOptionPicker } from '../shared/DotOptionPicker'
import { IssueStatusPill } from '../shared/IssueStatusPill'
import {
  PRIORITY_DOT_CLASS,
  PRIORITY_LABELS,
  PRIORITY_OPTIONS,
  STATUS_DOT_CLASS,
  STATUS_LABELS,
  formatDateTime,
} from '../shared/IssueRow'
import { IssueMentionsCard } from './IssueMentions'
import type { SystemIssue, SystemIssueStatus } from '../../types/system-issues'

const STATUS_OPTIONS = (Object.keys(STATUS_LABELS) as SystemIssueStatus[]).map((s) => ({
  value: s,
  label: STATUS_LABELS[s],
  dotClass: STATUS_DOT_CLASS[s],
}))
const PRIORITY_PICK = PRIORITY_OPTIONS.map((p) => ({
  value: p,
  label: PRIORITY_LABELS[p],
  dotClass: PRIORITY_DOT_CLASS[p],
}))

const LINK =
  'inline-flex items-center gap-1 text-[13px] font-medium text-blue-700 hover:underline dark:text-blue-400'

/**
 * Topo da conversa do chamado, compacto para sobrar espaço ao chat: numa
 * linha, voltar para a fila, #id, título + status e Resolver (dev) / Reabrir
 * (quem vê, se resolvido); embaixo, os atalhos do processo como links; e os
 * cards Atribuído a · Status · Prioridade · Reportado por · Aberto em ·
 * Colegas marcados. Dev troca atribuição, status e prioridade pelo card.
 */
export function IssueThreadHeader({
  issue,
  onBack,
  canManage,
  compact = false,
  onResolved,
}: {
  issue: SystemIssue
  onBack: () => void
  canManage: boolean
  /** Painel estreito ("Relatar problema"): menos respiro, cards em 2 colunas. */
  compact?: boolean
  /** Acabou de resolver (botão ou card de status): a conversa abre o resumo. */
  onResolved?: () => void
}) {
  const updateStatus = useUpdateIssueStatus(issue.id)
  const updatePriority = useUpdateIssuePriority(issue.id)
  const assign = useAssignIssue(issue.id)
  const reopen = useReopenIssue(issue.id)
  const [panel, setPanel] = useState<'assign' | 'status' | 'priority' | null>(null)
  const assignRef = useRef<HTMLButtonElement>(null)
  const statusRef = useRef<HTMLButtonElement>(null)
  const priorityRef = useRef<HTMLButtonElement>(null)

  const resolved = issue.status === 'resolved'
  const busy = updateStatus.isLoading || reopen.isLoading
  const assignee = issue.assigned_to
  const urgent = issue.priority === 'urgent'
  const editable = (ref: typeof assignRef, key: NonNullable<typeof panel>) =>
    canManage ? { buttonRef: ref, onClick: () => setPanel(key) } : {}

  return (
    <div className={`border-b border-border-subtle bg-surface pb-5 pt-5 ${compact ? 'px-4' : 'px-8'}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-3">
          <div className="min-w-0">
            {/* Voltar em texto ("‹ Chamados"), na linha do título: o botão
                quadrado já é o do cabeçalho da página (volta a Ocorrências). */}
            <div className="flex min-h-10 flex-wrap items-center gap-x-3 gap-y-1">
              <TextBackLink label="Chamados" onClick={onBack} />
              <span className="h-5 w-px bg-border-subtle" aria-hidden />
              <span className="font-mono text-sm text-muted">#{issue.id}</span>
              <h2
                className={`min-w-0 font-bold tracking-tight text-body [overflow-wrap:anywhere] ${
                  compact ? 'text-lg leading-7' : 'text-2xl leading-8'
                }`}
              >
                {issue.title}
              </h2>
              <IssueStatusPill status={issue.status} />
            </div>
            {issue.related_processes.length > 0 && (
              // Cada processo: o número abre a página dele; o balão, as
              // Ocorrências — a equipe já vê qual processo tem problema.
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                {issue.related_processes.map((p) => {
                  const number = p.process_number ?? `#${p.id}`
                  return (
                    <span key={p.id} className="inline-flex items-center gap-1.5">
                      <a
                        href={`/processo/${p.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Página do processo"
                        className={`${LINK} font-mono`}
                      >
                        <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                        {number}
                      </a>
                      <a
                        href={`/occurrences/${p.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Ocorrências do processo ${number}`}
                        title="Ocorrências do processo"
                        className={LINK}
                      >
                        <MessageSquare className="h-3.5 w-3.5" aria-hidden />
                      </a>
                    </span>
                  )
                })}
              </div>
            )}
          </div>
        </div>
        {resolved ? (
          // Reabrir: quem vê o chamado (o mesmo problema voltou).
          <button
            type="button"
            disabled={busy}
            onClick={() =>
              reopen.mutate(undefined, { onError: errorToast('Não foi possível reabrir o chamado.') })
            }
            className="inline-flex h-10 shrink-0 items-center gap-2 rounded-lg bg-gray-500 px-5 text-sm font-semibold text-white transition hover:bg-gray-600 disabled:opacity-50"
          >
            <RotateCcw className="h-4 w-4" />
            Reabrir
          </button>
        ) : (
          canManage && (
            <button
              type="button"
              disabled={busy}
              onClick={() =>
                updateStatus.mutate('resolved', {
                  onSuccess: onResolved,
                  onError: errorToast('Não foi possível resolver o chamado.'),
                })
              }
              className="inline-flex h-10 shrink-0 items-center gap-2 rounded-lg bg-green-700 px-5 text-sm font-semibold text-white transition hover:bg-green-800 disabled:opacity-50"
            >
              <Check className="h-4 w-4" strokeWidth={2.5} />
              Resolver
            </button>
          )
        )}
      </div>

      <div
        // Colegas marcados só com a largura dos avatares: o resto fica para os
        // nomes dos outros cards.
        className={`mt-4 grid gap-3 ${
          compact
            ? 'grid-cols-2'
            : 'grid-cols-2 md:grid-cols-3 xl:grid-cols-[repeat(5,minmax(0,1fr))_auto]'
        }`}
      >
        <InfoCard label="Atribuído a" {...editable(assignRef, 'assign')}>
          {assignee ? (
            <span className="flex min-w-0 items-center gap-2">
              <Avatar name={assignee.full_name} picture={assignee.picture} size={24} />
              <span className="truncate" title={assignee.full_name}>
                {assignee.full_name}
              </span>
            </span>
          ) : (
            <span className="text-muted">{canManage ? 'Atribuir a…' : 'Não atribuído'}</span>
          )}
        </InfoCard>
        <InfoCard label="Status" {...editable(statusRef, 'status')}>
          <span className="flex items-center gap-2">
            <span className={`h-2 w-2 shrink-0 rounded-full ${STATUS_DOT_CLASS[issue.status]}`} aria-hidden />
            <span className="truncate">{STATUS_LABELS[issue.status]}</span>
          </span>
        </InfoCard>
        <InfoCard label="Prioridade" danger={urgent} {...editable(priorityRef, 'priority')}>
          {urgent ? (
            PRIORITY_LABELS.urgent
          ) : (
            <span className="flex items-center gap-2">
              <span className={`h-2 w-2 shrink-0 rounded-full ${PRIORITY_DOT_CLASS[issue.priority]}`} aria-hidden />
              {PRIORITY_LABELS[issue.priority]}
            </span>
          )}
        </InfoCard>
        <InfoCard label="Reportado por">{issue.created_by?.full_name ?? '—'}</InfoCard>
        <InfoCard label="Aberto em">{formatDateTime(issue.created_at)}</InfoCard>
        <IssueMentionsCard issue={issue} canManage={canManage} />
      </div>

      {panel === 'assign' && (
        <AssignDeveloperPicker
          currentId={issue.assigned_to_id}
          anchorRef={assignRef}
          onPick={(id) => assign.mutate(id, { onError: errorToast('Não foi possível atribuir.') })}
          onClose={() => setPanel(null)}
        />
      )}
      {panel === 'status' && (
        <DotOptionPicker
          options={STATUS_OPTIONS}
          current={issue.status}
          anchorRef={statusRef}
          onPick={(s) =>
            updateStatus.mutate(s, {
              onSuccess: s === 'resolved' ? onResolved : undefined,
              onError: errorToast('Não foi possível alterar o status.'),
            })
          }
          onClose={() => setPanel(null)}
        />
      )}
      {panel === 'priority' && (
        <DotOptionPicker
          options={PRIORITY_PICK}
          current={issue.priority}
          anchorRef={priorityRef}
          onPick={(p) =>
            updatePriority.mutate(p, { onError: errorToast('Não foi possível mudar a prioridade.') })
          }
          onClose={() => setPanel(null)}
        />
      )}
    </div>
  )
}
